// src/lib/educationApi.js
// 새싹 신규직원 교육챗봇: 레거시 education.js 내용을 React에서 그대로 사용하기 위한 API 래퍼
// - 텍스트 생성은 기존과 동일하게 callAi 사용
// - 시나리오(롤플레이) 생성 / 일반 질의 / 롤플레이 답변 피드백 3가지 메서드 제공
// - 프롬프트/매뉴얼은 원본을 보존하여 의미 변경 없음

import { callAi } from "./geminiClient";

/** 로그인 확인 (기존 패턴 준수) */
function ensureLoginOrThrow(lang) {
  const userStr = (typeof localStorage !== "undefined" && localStorage.getItem("user")) || "";
  const user = userStr ? JSON.parse(userStr) : null;
  if (!user) {
    alert(lang === "zh-CN" ? "请先登录后再使用。" : "로그인 후 사용해 주세요");
    if (typeof window !== "undefined") window.location.href = "/login";
    throw new Error("NOT_AUTHENTICATED");
  }
  return user;
}

function getLang() {
  return (typeof document !== "undefined" && document.documentElement?.lang) || "ko";
}

function buildResources(lang) {
  if (lang === "zh-CN") {
    const knowledgeBase = `
## ${process.env.REACT_APP_CN_SIKTAM_SAYAK} 及辅助胶囊服用方法
- **${process.env.REACT_APP_CN_SIKTAM_SAYAK}**:
  - **保管**: 冷藏或室温保存（避免阳光直射）。无需加热。
  - **服用法**: 建议每天3次（早、中、晚），餐前30分钟~1小时，间隔4~5小时服用。餐后服用亦可。
  - **服用量**: 服用前充分摇匀。
- **辅助胶囊 (${process.env.REACT_APP_CN_JIBANG_KILL}, ${process.env.REACT_APP_CN_DOKSO_KILL})**:
  - **服用法**: 与汤药一起餐前用150~200ml温水送服。
  - **早餐**: ${process.env.REACT_APP_CN_SIKTAM_SAYAK}
  - **午餐**: ${process.env.REACT_APP_CN_SIKTAM_SAYAK} + ${process.env.REACT_APP_CN_JIBANG_KILL}(绿色)
  - **晚餐**: ${process.env.REACT_APP_CN_SIKTAM_SAYAK} + ${process.env.REACT_APP_CN_DOKSO_KILL}(酒红色)
- **${process.env.REACT_APP_CN_SUMYEON_KILL} (黄色)**:
  - **服用法**: 原则上睡前1小时单独服用。
  - **例外**: 若睡眠时间不规律或压力频繁，可在早上起床后或晚餐后服用。
- **${process.env.REACT_APP_CN_JIBANG_KILL} 服用次数 (来院患者)**:
  - **正常 (内脏脂肪 5~7)**: 午餐 1次。
  - **临界性 (9~12)**: 早餐、午餐 2次。
  - **中重度 (12~15)**: 早餐、午餐、晚餐 3次。
- **${process.env.REACT_APP_CN_JIBANG_KILL} 服用次数 (非面对面患者)**:
  - **管理顺畅**: 午餐 1次。
  - **快速管理**: 早餐、午餐 2次。
  - **饮食管理困难 (饮酒/零食每周2次以上)**: 早餐、午餐、晚餐 3次。

## 药物种类及阶段说明 (机密)
- **${process.env.REACT_APP_CN_JIBANG_SAYACK} (1~8阶段)**: 成分相同但浓度不同。药袋上以字母标记。
  - a(2), sbo(3), sb(4), sco(5), sc(6), sdo(7), sd(8)
- **${process.env.REACT_APP_CN_JIBANG_SAYACK} MAX**: 在现有 ${process.env.REACT_APP_CN_JIBANG_SAYACK} 基础上添加减肥成分的强化型。即使8阶段减重也受限时，经院长诊疗后处方。
- **${process.env.REACT_APP_SIKTAM_SAYAK} (汤药)**: 1~12阶段。
- **${process.env.REACT_APP_SIKTAM_SAYAK} (丸药)**: 1~7阶段。携带方便。以英语标记。
  - NA0(1), NA(2), NBO(3), NB(4), NCO(5), NC(6), NC+(7)

## 韩药药效维持期
- **维持期**: 最少6个月 ~ 最长1年。
- **停药3个月以上时**: 现有药物保管，建议经院长诊疗后重新处方适合当前状态的药物。
- **超过1年以上时**: 为了药效和安全性，建议废弃。
- **因怀孕/分娩长期未服用时**: 附上证明书（超声波、宝宝照片等）来院时，按剩余药物天数更换新药处方。（但，分娩后6~8周起可服用）
- **怀疑有沉淀物/变质时**: 药效期内不是问题，但若服用不便，联系前台时立即处理。

## 预约规则
- **复诊预约**:
  - **10点~5点**: 每小时最多2组（同行视为1组）。
  - **5点30分, 6点 (回访时间)**: 不可复诊预约。
  - **6点30分~8点**: 最多4组。
- **初诊/再结算预约**:
  - 每小时最多2组。（再结算视为初诊）
  - 但，若有仅剩院长咨询的患者，可追加1组。
- **注意事项**: 根据当日预约情况、连休、活动期间规则可能变动，变动时院长当日通知。

## 前台主要业务
- **CRM 相关**:
  - 诊疗时间内确认 CRM 预约后登记到 HanChart，传达问卷及联系。
  - 初诊流入顾客（新规、来院、重复、在线咨询）电话联系。
  - 填写问卷的顾客信息转移到 HanChart。
  - 确认顾客状态及登记配送。
- **来院/诊疗 相关**:
  - 管理来院咨询顺序。
  - 确认 InBody 设备名称变更。
  - 来院顾客结算接待。
  - 投诉接待及闲聊。
  - 无预约访问 (Walk-in) 患者接待。
  - 确认快递遗漏患者。
  - 非面对面患者信息录入快递 Excel。
  - 未接电话联系。
- **HanChart 相关**:
  - 诊疗前/中确认 Naver 预约。
  - 确认结算相关套餐。
  - Naver 初诊流入时联系。
  - 转让、套餐变更、未收款等图表整理。
- **截止 相关**:
  - 填写截止账簿及结算。
  - 记录入账及支出明细。
  - 确认初诊/复诊/再结算收款明细。
  - 确认截止表格整理状态。

## 开店准备程序
1. 开启电脑: 开启前台、咨询室、副院长室电脑并运行 Chrome (表格、开店、截止)、Calli、CRM、HanChart。
2. 检查室准备: 开启指数检查电脑并连接 lookinbody (InBody) 应用程序管理员版。
3. 环境整理: 咨询室玻璃酒精清洁、播放歌曲、茶水间整理。
4. 预约及未接确认: 确认未接电话并记载于 Excel，确认 CRM/Naver 预约后在 HanChart 登记顾客。
5. 药材室库存掌握: 不足药物从 413 号取来。

## 截止整理程序
1. 填写截止 Excel: 确认 CRM、HanChart 结算后将账簿内容填写到截止 Excel。
2. 填写截止贴纸: 粘贴截止 Excel 内容并输入日销售额、流入路径等追加内容。
3. 环境整理 (7点30分): 茶水间整理、咖啡机清洁、垃圾分类、运行扫地机器人、清空垃圾桶。
4. 电源 OFF: 关闭前台、咨询室、副院长室、检查室电脑及 TV、空调。
5. 报告: 打印结算收据，整理传达事项后与截止贴纸一起通过 KakaoTalk 传达给院长后截止聊天。

## 初诊/复诊/再结算 概念整理
- **初诊**: 首次利用 ${process.env.REACT_APP_CN_CLINIC_NAME} 的顾客。
  - 来院程序: 前台进行 InBody 及指数检查 -> 连接咨询组长 -> 咨询后组长向院长聊天。
- **复诊**: 有医院利用经验，且有剩余药物的顾客。
  - 来院程序: InBody 检查 -> 确认剩余药物 -> 向院长聊天 ("院长，复诊来院 OOO 您在。可以请进来吗？")。
- **再结算**: 有医院利用经验，但没有剩余药物的顾客。
  - 套餐决定完成时: 立即向院长聊天 ("院长，再结算来院 OOO 您在。可以请进来吗？")。
  - 套餐决定未完成时: 向咨询老师连接咨询。

## KakaoTalk 剩余药物申请接待
- **情况**: 因体重变化、不适症状等各种原因接到 KakaoTalk 咨询时。
- **初期应对**:
  1. 从 'Open Script' 的 '剩余药物表格' 复制以下基本指南信息并发送。
  2. 在 HanChart 确认患者姓名和最后服用药物 (${process.env.REACT_APP_CN_JIBANG_SAYACK}/${process.env.REACT_APP_CN_SIKTAM_SAYAK}) 及阶段后修改发送。
  - **基本指南信息**: "您好 OOO 您😃 最后服用的药物阶段是 [${process.env.REACT_APP_CN_JIBANG_SAYACK}/${process.env.REACT_APP_CN_SIKTAM_SAYAK}] [N]阶段，若希望不变更阶段处方半个月的药，可以直接处方，但是 1) 若希望咨询药物或为了提高药物强度希望提升阶段，需要院长咨询。帮您预约代表院长咨询吗？😊😊 2) 若希望无变更同样处方，也请务必留下 '阶段同样进行' 的回复 💗💗 留下号码或回复，我们将帮您进行下一步 🥰"
- **根据患者回复的后续措施**:
  - **"1" 或请求咨询时**:
    - **非面对面患者**: "即使麻烦，请在下方 Naver 预约您方便通话的时间，我们将给您电话咨询 😊😊 复诊预约 https://han.gl/RCkFt"
    - **来院患者**: "即使麻烦，请在下方链接预约您方便来院的时间，我们将帮您确认 😊😊 复诊预约 https://han.gl/RCkFt"
  - **"2" 或请求相同处方时**:
    1. 在 HanChart 确认以前处方是 '半月份' 还是 '一月份'。
    2. 复制 KakaoTalk '备忘录输入' 栏的地址/联系方式内容发送给患者确认是否有误。
    3. 发送快递费指南及入款请求信息。
    - **入款指南信息**: "入款指南 📌 企业银行 ( 户主 : 김재윤 ) 216-134102-04-015 3,500韩元 ♥ 下午4点前入款时当日发送⚡ ♥ 入款人名义不同时，请务必❗ 告知 ♥ 入款后说一声 '完成' 可以更~ 迅速处理😄"
    4. 若以前处方是 '一月份'，追加 "帮您同样进行阶段1个月份处方" 的指南。

## HanChart 和 CRM 说明
- **HanChart**: 负责实质性诊疗和预约管理的图表。
- **CRM**: 初诊顾客管理及处理所有顾客收款的专用图表。

## 缩略语及记录方法
- **缩略语**: ${process.env.REACT_APP_CN_JIBANG_SAYACK}(zs), ${process.env.REACT_APP_CN_SIKTAM_SAYAK}(ss), ${process.env.REACT_APP_SUMYEON_KILL}(sk), ${process.env.REACT_APP_CN_JIBANG_KILL}(zk), ${process.env.REACT_APP_CN_DOKSO_KILL}(dk), sv보석환(sv), sv${process.env.REACT_APP_CN_JIBANG_KILL}两周.
- **服务药**: 评论服务不记录，介绍服务包含在记录中。
- **记录顺序**: ${process.env.REACT_APP_CN_JIBANG_SAYACK}/${process.env.REACT_APP_CN_SIKTAM_SAYAK} > ${process.env.REACT_APP_SUMYEON_KILL} > ${process.env.REACT_APP_CN_JIBANG_KILL} > ${process.env.REACT_APP_CN_DOKSO_KILL} 顺序记载。
- **记录方法**:
  1. 院长在 HanChart 用红字给出处方明细。
  2. 复制红字在上方栏用黑字粘贴。
  3. [] 内写负责人姓名，换行后用黑字记载内容。
  4. 以 '处方药物天数 / 总处方天数' 形式记录 (例: 30/90)。

## Naver 预约确认
- **确认时点**: 不确认其他日期，仅早上确认当日预约。
- **确认对象**: HanChart以此为准标记绿色 'N' 的预约。
- **处理程序**:
  1. 确认非面对面/来院与否后变更。
  2. 确认结算/剩余药物与否后变更。
  3. 初诊预约时: 传达问卷 -> 电话联系确认来院及 KakaoTalk 频道流入指南 -> 在 CRM 登记预约。
  4. 确认预约备忘录后记载于 HanChart 主诉症 (例: '请提升阶段' -> '阶段上调').
    `.trim();

    const systemPrompt = "你是 '" + process.env.REACT_APP_CN_CLINIC_NAME + "' 的 AI 睿智萍。请仅根据下面提供的业务手册回答新员工的问题。手册中没有的内容请诚实回答不知道，请使用亲切、详细且易懂的资深员工语气。请用中文回答。";

    const rolePlayPrompt = "你是 '" + process.env.REACT_APP_CN_CLINIC_NAME + "' 的 AI 睿智萍。请看下面给出的患者场景和员工的回答，根据提供的业务手册具体反馈员工回答的优点和需要改进的地方。请用亲切鼓励的资深员工语气回答。";

    const scenarioPrompt = "你是一家减肥专业韩医院的挑剔患者。请用一两句简短具体的话提出新员工难以应对的投诉或提问情况。（例：抱怨吃药也没效果、要求退款、诉说意想不到的副作用等）";

    return { knowledgeBase, systemPrompt, rolePlayPrompt, scenarioPrompt };
  }

  // ----- 한국어(기본) -----
  const knowledgeBase = `
## ${process.env.REACT_APP_SIKTAM_SAYAK} 및 보조캡슐 복용법
- **${process.env.REACT_APP_SIKTAM_SAYAK}**:
  - **보관**: 냉장 또는 실온 보관 (직사광선 피하기). 중탕 불필요.
  - **복용법**: 하루 3회(아침, 점심, 저녁), 식사 30분~1시간 전, 4~5시간 간격으로 복용 권장. 식후 복용도 가능.
  - **복용량**: 복용 전 충분히 흔들어서 복용.
- **보조캡슐 (뱃살킬, ${process.env.REACT_APP_DOKSO_KILL})**:
  - **복용법**: 탕약과 함께 식전에 미지근한 물 150~200ml와 복용.
  - **아침**: ${process.env.REACT_APP_SIKTAM_SAYAK}
  - **점심**: ${process.env.REACT_APP_SIKTAM_SAYAK} + 뱃살킬(초록색)
  - **저녁**: ${process.env.REACT_APP_SIKTAM_SAYAK} + ${process.env.REACT_APP_DOKSO_KILL}(와인색)
- **${process.env.REACT_APP_SUMYEON_KILL} (노란색)**:
  - **복용법**: 잠자기 1시간 전 단독 복용 원칙.
  - **예외**: 수면 시간이 불규칙하거나 스트레스가 잦은 경우, 아침 기상 후 또는 저녁 식사 후 복용 가능.
- **뱃살킬 복용 횟수 (내원 환자)**:
  - **정상 (내장지방 5~7)**: 점심 1회.
  - **경계성 (9~12)**: 아침, 점심 2회.
  - **중증도 (12~15)**: 아침, 점심, 저녁 3회.
- **뱃살킬 복용 횟수 (비대면 환자)**:
  - **원활한 관리**: 점심 1회.
  - **빠른 관리**: 아침, 점심 2회.
  - **식단 관리 어려움 (음주/군것질 주 2회 이상)**: 아침, 점심, 저녁 3회.

## 약 종류 및 단계 설명 (대외비)
- **${process.env.REACT_APP_JIBANG_SAYACK} (1~8단계)**: 성분은 동일하나 농축도 차이. 알파벳으로 약 봉투에 표기.
  - a(2), sbo(3), sb(4), sco(5), sc(6), sdo(7), sd(8)
- **${process.env.REACT_APP_JIBANG_SAYACK} MAX**: 기존 ${process.env.REACT_APP_JIBANG_SAYACK}에 다이어트 성분 추가된 강화형. 8단계로도 감량이 제한될 때 원장님 진료 후 처방.
- **${process.env.REACT_APP_SIKTAM_SAYAK} (탕약)**: 1~12단계.
- **${process.env.REACT_APP_SIKTAM_SAYAK} (환약)**: 1~7단계. 휴대가 간편. 영어로 표기.
  - NA0(1), NA(2), NBO(3), NB(4), NCO(5), NC(6), NC+(7)

## 한약 약효 유지기간
- **유지기간**: 최소 6개월 ~ 최대 1년.
- **3개월 이상 휴약 시**: 기존 약은 보관하고, 원장님 진료 후 현재 상태에 맞는 약으로 새로 처방 권장.
- **1년 이상 경과 시**: 약효와 안정성을 위해 폐기 권고.
- **임신/출산으로 장기 미복용 시**: 증명서(초음파, 아기사진 등) 첨부하여 내원 시, 남은 약 일수만큼 새 약으로 교환 처방. (단, 출산 후 6~8주 지난 시점부터 복용 가능)
- **침전물/변질 의심 시**: 약효 기간 내에는 문제가 아니나, 복용이 불편할 경우 데스크 연락 시 즉시 조치.

## 예약 규칙
- **재진 예약**:
  - **10시~5시**: 시간당 2팀까지 가능 (동행은 1팀으로 간주).
  - **5시30분, 6시 (해피콜 타임)**: 재진 예약 불가.
  - **6시30분~8시**: 4팀까지 가능.
- **초진/재결제 예약**:
  - 시간당 2팀까지 가능. (재결제는 초진으로 간주)
  - 단, 원장님 상담만 남은 환자가 있을 경우 1팀 추가 가능.
- **주의사항**: 당일 예약 상황, 연휴, 행사 기간에 따라 규칙은 변동될 수 있으며, 변동 시 원장님이 당일 공지.

## 데스크 주요 업무
- **CRM 관련**:
  - 진료 시간 내 CRM 예약 확인 후 한차트에 등록, 설문지 전달 및 컨택.
  - 초진 유입 고객(신규, 내원, 중복, 온라인 상담) 전화 컨택.
  - 설문지 작성 고객 정보 한차트로 이전.
  - 고객 상태 확인 및 배송 등록.
- **내원/진료 관련**:
  - 내원 상담 순서 관리.
  - 인바디 기기 이름 변경 확인.
  - 내원 고객 결제 응대.
  - 컴플레인 응대 및 스몰토크.
  - 워크인(예약 없는 방문) 환자 응대.
  - 택배 누락 환자 확인.
  - 비대면 환자 정보 택배 엑셀에 기입.
  - 부재중 전화 컨택.
- **한차트 관련**:
  - 진료 전/중 네이버 예약 확인.
  - 결제 관련 패키지 확인.
  - 네이버 초진 유입 시 컨택.
  - 양도, 패키지 변경, 미수금 등 차트 정리.
- **마감 관련**:
  - 마감 장부 작성 및 정산.
  - 입금 및 지출 내역 기록.
  - 초진/재진/재결제 수납 내역 확인.
  - 마감 시트 정리 상태 확인.

## 오픈 준비 절차
1. 컴퓨터 켜기: 데스크, 상담실, 부원장실 컴퓨터 켜고 크롬(시트, 오픈, 마감), 콜리, CRM, 한차트 실행.
2. 검사실 준비: 지수검사 컴퓨터 켜고 lookinbody(인바디) 어플 관리자용으로 연결.
3. 환경 정리: 상담실 유리 알콜 청소, 노래 켜기, 탕비실 정리.
4. 예약 및 부재중 확인: 부재중 전화 확인하여 엑셀 기재, CRM/네이버 예약 확인 후 한차트에 고객 등록.
5. 약재실 재고 파악: 부족한 약은 413호에서 가져오기.

## 마감 정리 절차
1. 마감 엑셀 작성: CRM, 한차트 정산 확인 후 장부 내용을 마감 엑셀에 작성.
2. 마감 스티커 작성: 마감 엑셀 내용 붙여넣고 일 매출, 유입경로 등 추가 내용 입력.
3. 환경 정리 (7시 30분): 탕비실 정리, 커피머신 청소, 분리수거, 로봇청소기 작동, 휴지통 비우기.
4. 전원 OFF: 데스크, 상담실, 부원장실, 검사실 컴퓨터 및 TV, 에어컨 끄기.
5. 보고: 정산 영수증 출력, 전달 사항 정리하여 마감 스티커와 함께 원장님께 카톡 전달 후 마감 채팅.

## 초진/재진/재결제 개념 정리
- **초진**: '${process.env.REACT_APP_CLINIC_NAME}'을 처음 이용하시는 분.
  - 내원 절차: 데스크에서 인바디 및 지수검사 -> 상담팀장 연결 -> 상담 후 팀장님이 원장님께 채팅.
- **재진**: 한의원 이용 경험이 있고, 남은 약이 있는 분.
  - 내원 절차: 인바디 검사 -> 남은 약 확인 -> 원장님께 채팅("원장님 재진 내원 OOO님 계십니다. 모셔도 될까요?").
- **재결제**: 한의원 이용 경험이 있고, 남은 약이 없는 분.
  - 패키지 결정 완료 시: 원장님께 바로 채팅("원장님 재결제 내원 OOO님 계십니다. 모셔도 될까요?").
  - 패키지 결정 미완료 시: 상담 선생님께 상담 연결.

## 카카오톡 남은 약 신청 응대
- **상황**: 몸무게 변화, 불편 증상 등 다양한 이유로 카톡 문의가 올 경우.
- **초기 대응**:
  1. '오픈스크립트'의 '남은약 시트'에서 아래 기본 안내 메시지를 복사하여 발송.
  2. 환자 이름과 마지막 복용 약(${process.env.REACT_APP_JIBANG_SAYACK}/${process.env.REACT_APP_SIKTAM_SAYAK}) 및 단계를 한차트에서 확인하여 수정 후 발송.
  - **기본 안내 메시지**: "안녕하세요 OOO님😃 마지막으로 복용하신 약 단계는 [${process.env.REACT_APP_JIBANG_SAYACK}/${process.env.REACT_APP_SIKTAM_SAYAK}] [N]단계로 복용을 하셨는데 단계 변경 없이 보름치 약 처방 원하시면 바로 처방받으실 수 있지만 1) 약에 대해 상담 받길 원하시거나 약의 강도를 높이기 위해 단계를 올리길 원하시면 원장님 상담이 필요하세요. 대표원장님 상담 예약을 도와드릴까요? 😊😊 2) 변경 없이 동일하게 처방 원하실 경우에도 '단계 동일하게 진행' 답변을 꼬~옥 남겨주세요 💗💗 번호 혹은 답변을 남겨주시면 다음 진행 도와드리겠습니다 🥰"
- **환자 답변에 따른 후속 조치**:
  - **"1" 또는 상담 요청 시**:
    - **비대면 환자**: "번거로우시더라도 통화가 편한 시간을 아래 네이버예약 해주시면 전화상담 드릴께요 😊😊 재진 예약 https://han.gl/RCkFt"
    - **내원 환자**: "번거로우시더라도 내원하기 편한 시간을 아래 링크로 예약 해주시면 확인 도와드릴게요 😊😊 재진 예약 https://han.gl/RCkFt"
  - **"2" 또는 동일 처방 요청 시**:
    1. 한차트에서 이전 처방이 '보름분'이었는지 '한달분'이었는지 확인.
    2. 카카오톡 '메모 입력' 칸의 주소/연락처 내용을 복사하여 환자에게 발송하며 이상 없는지 확인.
    3. 택배비 안내 및 입금 요청 메시지 발송.
    - **입금 안내 메시지**: "입금 안내 드려요 📌 기업은행 ( 예금주 : 김재윤 ) 216-134102-04-015 3,500원 ♥ 오후 4시 이전 입금시 당일 발송⚡ ♥ 입금자 명이 다를 경우, 꼭❗ 말씀 해주세요 ♥ 입금 하신뒤 '완료' 라고 말씀해주시면 더~ 신속한 처리가 가능해요😄"
    4. 이전 처방이 '한달분'이었을 경우, "단계 동일하게 1개월분 처방 도와드리겠다"는 안내 추가.

## 한차트와 CRM 설명
- **한차트**: 실질적인 진료와 예약 관리를 담당하는 차트.
- **CRM**: 초진 고객 관리 및 모든 고객의 수납을 처리하는 전용 차트.

## 약어 및 차팅 방법
- **약어**: ${process.env.REACT_APP_JIBANG_SAYACK}(zs), ${process.env.REACT_APP_SIKTAM_SAYAK}(ss), ${process.env.REACT_APP_SUMYEON_KILL}(sk), ${process.env.REACT_APP_JIBANG_KILL}(zk), ${process.env.REACT_APP_DOKSO_KILL}(dk), sv보석환(sv), sv뱃2주.
- **서비스 약**: 리뷰 서비스는 차팅 안함, 소개 서비스는 차팅에 포함.
- **차팅 순서**: ${process.env.REACT_APP_JIBANG_SAYACK}/${process.env.REACT_APP_SIKTAM_SAYAK} >  ${process.env.REACT_APP_SUMYEON_KILL} >  ${process.env.REACT_APP_JIBANG_KILL} > ${process.env.REACT_APP_DOKSO_KILL} 순으로 기재.
- **차팅 방법**:
  1. 원장님이 한차트에 빨간 글씨로 처방 내역을 주심.
  2. 빨간 글씨를 복사하여 윗 칸에 검정 글씨로 붙여넣기.
  3. [] 안에 담당자 이름 적고, 줄 내린 후 검은 글씨로 내용 기재.
  4. '처방받은 약 일수 / 총 처방 일수' 형태로 기록 (예: 30/90).

## 네이버 예약 확인
- **확인 시점**: 다른 날짜는 확인하지 않고, 당일 예약만 아침에 확인.
- **확인 대상**: 한차트 기준 초록색 'N' 표시가 된 예약.
- **처리 절차**:
  1. 비대면/내원 여부 확인 후 변경.
  2. 결제/남은 약 여부 확인 후 변경.
  3. 초진 예약 시: 설문지 전달 -> 유선 연락으로 내원 확인 및 카톡 채널 유입 안내 -> CRM에 예약 등록.
  4. 예약 메모 확인 후 한차트 주소증에 기재 (예: '단계 올려주세요' -> '단계상향').
  `.trim();

  const systemPrompt = "당신은 '" + process.env.REACT_APP_CLINIC_NAME + "'의 AI 예지핑 입니다. 신입 직원의 질문에 대해 아래 제공된 업무 매뉴얼만을 바탕으로 답변해주세요. 매뉴얼에 없는 내용은 모른다고 솔직하게 답하고, 친절하고 상세하며 이해하기 쉽게 선임 직원의 말투를 사용해주세요. 답변은 한국어로 해주세요.";

  const rolePlayPrompt = "당신은 '" + process.env.REACT_APP_CLINIC_NAME + "'의 AI 예지핑 입니다. 아래 주어진 환자 시나리오와 직원의 답변을 보고, 제공된 업무 매뉴얼에 근거하여 직원의 답변이 잘한 점과 개선할 점을 구체적으로 피드백해주세요. 친절하고 격려하는 선임 직원의 말투로 답변해주세요.";

  const scenarioPrompt = "당신은 다이어트 전문 한의원에 내원한 까다로운 환자입니다. 신입 직원이 응대하기 어려운 컴플레인이나 질문 상황을 한두 문장으로 짧고 구체적으로 제시해주세요. (예: 약을 먹어도 효과가 없다는 불만, 환불 요구, 예상치 못한 부작용 호소 등)";

  return { knowledgeBase, systemPrompt, rolePlayPrompt, scenarioPrompt };
}

/** 공통 컨텐츠 래핑 */
function wrapAsUserPrompt(text) {
  return [{ role: "user", parts: [{ text }] }];
}

export const EducationAI = {
  /** 일반 질문 응답 */
  async ask({ query }) {
    const lang = getLang();
    const user = ensureLoginOrThrow(lang);
    const { knowledgeBase, systemPrompt } = buildResources(lang);

    const prompt =
      lang === "zh-CN"
        ? `${systemPrompt}

    ### 业务手册:
    ${knowledgeBase}

    ### 新员工提问:
    "${query}"`
            : `${systemPrompt}

    ### 업무 매뉴얼:
    ${knowledgeBase}

    ### 신입 직원의 질문:
    "${query}"`;

    const contents = wrapAsUserPrompt(prompt);
    const data = await callAi({
      contents,
      api_name: "education_chatbot",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });

    const answer = data?.data?.answer ?? data?.answer ?? data?.text ?? data?.data?.text;
    if (!answer) throw new Error("응답 형식이 올바르지 않습니다.");
    return { answer };
  },

  /** 롤플레이 피드백 */
  async feedback({ scenario, answerFromStaff }) {
    const lang = getLang();
    const user = ensureLoginOrThrow(lang);
    const { knowledgeBase, rolePlayPrompt } = buildResources(lang);

    const prompt =
      lang === "zh-CN"
        ? `${rolePlayPrompt}

    ### 患者场景
    ${scenario}

    ### 员工回答
    ${answerFromStaff}

    ### 业务手册
    ${knowledgeBase}`
            : `${rolePlayPrompt}

    ### 환자 시나리오
    ${scenario}

    ### 직원의 답변
    ${answerFromStaff}

    ### 업무 매뉴얼
    ${knowledgeBase}`;

    const contents = wrapAsUserPrompt(prompt);
    const data = await callAi({
      contents,
      api_name: "education_chatbot_roleplay",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });

    const feedback = data?.data?.answer ?? data?.answer ?? data?.text ?? data?.data?.text;
    if (!feedback) throw new Error("응답 형식이 올바르지 않습니다.");
    return { feedback };
  },

  /** 롤플레이용 시나리오 생성 */
  async scenario() {
    const lang = getLang();
    const user = ensureLoginOrThrow(lang);
    const { scenarioPrompt } = buildResources(lang);

    const contents = wrapAsUserPrompt(scenarioPrompt);
    const data = await callAi({
      contents,
      api_name: "education_chatbot_scenario",
      userId: user.id,
      generationConfig: null,
      version: undefined,
    });

    const scenario = data?.data?.answer ?? data?.answer ?? data?.text ?? data?.data?.text;
    if (!scenario) throw new Error("응답 형식이 올바르지 않습니다.");
    return { scenario };
  },
};

export { getLang, buildResources };
